import React, { useState, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, AppBar, Toolbar, Typography, Container, Box, Tab, Tabs, IconButton, Divider, Chip, CircularProgress, Button } from '@mui/material';
import { LibraryMusic, MusicNote, QrCode, Palette, Brightness4, Brightness7, QueueMusic, Home } from '@mui/icons-material';
import { useSongStoreSelector } from './hooks/useSongStoreSelector';
import { useThemeStore } from './stores/themeStore';
import { useSessionStore } from './stores/sessionStore';
import { ErrorBoundary } from './components/ErrorBoundary';
import { StoreHydrationGate } from './components/StoreHydrationGate';

// Lazy load components for better performance
const Homepage = lazy(() => import('./components/Homepage').then(m => ({ default: m.Homepage })));
const LibraryLayout = lazy(() => import('./components/LibraryLayout').then(m => ({ default: m.LibraryLayout })));
const PerformLayout = lazy(() => import('./components/PerformLayout').then(m => ({ default: m.PerformLayout })));
const PerformanceSetup = lazy(() => import('./components/PerformanceSetup').then(m => ({ default: m.PerformanceSetup })));
const SessionCreator = lazy(() => import('./components/SessionCreator').then(m => ({ default: m.SessionCreator })));
const AudienceJoin = lazy(() => import('./components/AudienceJoin').then(m => ({ default: m.AudienceJoin })));
const SongLibrary = lazy(() => import('./components/SongLibrary').then(m => ({ default: m.SongLibrary })));
const SongView = lazy(() => import('./components/SongView').then(m => ({ default: m.SongView })));
const SongViewWrapper = lazy(() => import('./components/SongViewWrapper').then(m => ({ default: m.SongViewWrapper })));
const ThemeCustomizer = lazy(() => import('./components/ThemeCustomizer').then(m => ({ default: m.ThemeCustomizer })));
const SetlistManager = lazy(() => import('./components/SetlistManager').then(m => ({ default: m.SetlistManager })));
const SetlistEditor = lazy(() => import('./components/SetlistEditor').then(m => ({ default: m.SetlistEditor })));
const SongEditorPage = lazy(() => import('./components/SongEditorPage'));
const DataMigration = lazy(() => import('./components/DataMigration').then(m => ({ default: m.DataMigration })));

// Loading component
const Loading: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress />
  </Box>
);

const Navigation: React.FC = () => {
  const location = useLocation();
  const { currentSession } = useSessionStore();
  
  const getTabValue = () => {
    if (location.pathname === '/') return 0;
    if (location.pathname.startsWith('/library')) return 1;
    if (location.pathname.startsWith('/setlist')) return 2;
    if (location.pathname.startsWith('/perform')) return 3;
    if (location.pathname.startsWith('/join')) return -1; // No tab for join pages
    return 0; // Default to home tab
  };
  
  const isAudienceView = location.pathname.startsWith('/join');
  
  // For audience members - show simplified navigation
  if (isAudienceView) {
    return (
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
          <Tabs value={0} aria-label="audience navigation" sx={{ flexGrow: 1 }}>
            <Tab 
              label="Home" 
              icon={<Home />} 
              component={Link} 
              to="/" 
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
          {currentSession && (
            <Chip 
              label="In Session" 
              color="success" 
              size="small" 
              icon={<MusicNote />}
            />
          )}
        </Box>
      </Box>
    );
  }
  
  // For musicians - show full navigation
  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
        <Tabs value={getTabValue()} aria-label="main navigation">
          <Tab 
            label="Home" 
            icon={<Home />} 
            component={Link} 
            to="/" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            label="Song Library" 
            icon={<LibraryMusic />} 
            component={Link} 
            to="/library" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            label="Setlists" 
            icon={<QueueMusic />} 
            component={Link} 
            to="/setlists" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            label="Perform" 
            icon={<QrCode />} 
            component={Link} 
            to="/perform" 
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
        
        {/* Show session status */}
        {currentSession && (
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Chip 
              label="Live Session" 
              color="success" 
              size="small" 
              icon={<MusicNote />}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

function App() {
  const { mode, colors, toggleMode } = useThemeStore();
  const [isThemeCustomizerOpen, setIsThemeCustomizerOpen] = useState(false);
  const [migrationComplete, setMigrationComplete] = useState(false);
  
  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: { main: colors[mode].primary },
      secondary: { main: colors[mode].secondary },
      success: { main: colors[mode].success },
      warning: { main: colors[mode].warning },
      error: { main: colors[mode].error },
      info: { main: colors[mode].info },
      background: { 
        default: colors[mode].background,
        paper: colors[mode].paper
      },
      text: {
        primary: colors[mode].text.primary,
        secondary: colors[mode].text.secondary,
        disabled: colors[mode].text.disabled
      },
      divider: colors[mode].divider
    },
    typography: {
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
  }), [mode, colors]);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <StoreHydrationGate>
          {/* Skip navigation links for accessibility */}
          <Box 
            component="a" 
            href="#main-content"
            sx={{ 
              position: 'absolute', 
              left: '-9999px',
              backgroundColor: 'background.paper',
              padding: 1,
              zIndex: 9999,
              '&:focus': { 
                position: 'fixed',
                top: 10,
                left: 10,
                textDecoration: 'none',
                color: 'primary.main',
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 1
              } 
            }}
          >
            Skip to main content
          </Box>
          {!migrationComplete && (
            <ErrorBoundary>
              <Suspense fallback={null}>
                <DataMigration onComplete={() => setMigrationComplete(true)} />
              </Suspense>
            </ErrorBoundary>
          )}
          <AppBar position="static" elevation={1}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              CommonChord
            </Typography>
            <IconButton 
              color="inherit" 
              onClick={toggleMode}
              aria-label={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
            >
              {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
            </IconButton>
            <IconButton 
              color="inherit" 
              onClick={() => setIsThemeCustomizerOpen(true)}
              aria-label="Customize theme colors"
            >
              <Palette />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Navigation />
        
        <Box component="main" id="main-content" sx={{ outline: 'none' }} tabIndex={-1}>
          <Suspense fallback={<Loading />}>
            <Routes>
            {/* Homepage */}
            <Route path="/" element={
              <Container maxWidth="lg" sx={{ py: 3 }}>
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Homepage Error</Typography>
                    <Typography color="text.secondary">Failed to load homepage</Typography>
                    <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>Reload</Button>
                  </Box>
                }>
                  <Homepage />
                </ErrorBoundary>
              </Container>
            } />
            
            {/* Library routes with layout */}
            <Route path="/library" element={<LibraryLayout />}>
              <Route index element={
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Library Error</Typography>
                    <Typography color="text.secondary">Failed to load song library</Typography>
                    <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>Reload</Button>
                  </Box>
                }>
                  <SongLibrary />
                </ErrorBoundary>
              } />
              
              {/* Song routes nested under library */}
              <Route path="song/:id" element={
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Song Error</Typography>
                    <Typography color="text.secondary">Failed to load chord chart</Typography>
                    <Button component={Link} to="/library" sx={{ mt: 2 }}>Back to Library</Button>
                  </Box>
                }>
                  <SongViewWrapper />
                </ErrorBoundary>
              } />
              
              <Route path="song/:id/edit" element={
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Editor Error</Typography>
                    <Typography color="text.secondary">Failed to load song editor</Typography>
                    <Button component={Link} to="/library" sx={{ mt: 2 }}>Back to Library</Button>
                  </Box>
                }>
                  <SongEditorPage />
                </ErrorBoundary>
              } />
            </Route>
            
            {/* Setlist routes */}
            <Route path="/setlists" element={
              <Container maxWidth="lg" sx={{ py: 3 }}>
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Setlist Error</Typography>
                    <Typography color="text.secondary">Failed to load setlists</Typography>
                    <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>Reload</Button>
                  </Box>
                }>
                  <SetlistManager />
                </ErrorBoundary>
              </Container>
            } />
            
            <Route path="/setlist/:id" element={
              <Container maxWidth="lg" sx={{ py: 3 }}>
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Setlist Error</Typography>
                    <Typography color="text.secondary">Failed to load setlist editor</Typography>
                    <Button component={Link} to="/setlists" sx={{ mt: 2 }}>Back to Setlists</Button>
                  </Box>
                }>
                  <SetlistEditor />
                </ErrorBoundary>
              </Container>
            } />
            
            {/* Performance routes with layout */}
            <Route path="/perform" element={<PerformLayout />}>
              <Route index element={
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Performance Error</Typography>
                    <Typography color="text.secondary">Failed to load performance setup</Typography>
                    <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>Reload</Button>
                  </Box>
                }>
                  <PerformanceSetup />
                </ErrorBoundary>
              } />
              <Route path="live" element={
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Session Error</Typography>
                    <Typography color="text.secondary">Failed to load live session</Typography>
                    <Button component={Link} to="/perform" sx={{ mt: 2 }}>Back to Setup</Button>
                  </Box>
                }>
                  {/* TODO: Create LivePerformance component */}
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5">Live Performance</Typography>
                    <Typography color="text.secondary">Live performance view coming soon</Typography>
                  </Box>
                </ErrorBoundary>
              } />
            </Route>
            
            {/* Join routes */}
            <Route path="/join" element={
              <Container maxWidth="lg" sx={{ py: 3 }}>
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Join Error</Typography>
                    <Typography color="text.secondary">Failed to load join page</Typography>
                    <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>Reload</Button>
                  </Box>
                }>
                  <AudienceJoin />
                </ErrorBoundary>
              </Container>
            } />
            
            <Route path="/join/:sessionId" element={
              <Container maxWidth="lg" sx={{ py: 3 }}>
                <ErrorBoundary fallback={
                  <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error" gutterBottom>Join Error</Typography>
                    <Typography color="text.secondary">Failed to join session</Typography>
                    <Button component={Link} to="/join" sx={{ mt: 2 }}>Try Again</Button>
                  </Box>
                }>
                  <AudienceJoin />
                </ErrorBoundary>
              </Container>
            } />
            
            {/* Legacy route redirects */}
            <Route path="/host" element={<Navigate to="/perform" replace />} />
            
            {/* 404 handler */}
            <Route path="*" element={
              <Container maxWidth="lg" sx={{ py: 3 }}>
                <Box sx={{ textAlign: 'center', mt: 5 }}>
                  <Typography variant="h5" gutterBottom>Page Not Found</Typography>
                  <Typography color="text.secondary">
                    The page you're looking for doesn't exist.
                  </Typography>
                  <Button component={Link} to="/" sx={{ mt: 2 }}>
                    Go Home
                  </Button>
                </Box>
              </Container>
            } />
          </Routes>
        </Suspense>
        </Box>
        
        <Suspense fallback={null}>
          <ThemeCustomizer 
            open={isThemeCustomizerOpen} 
            onClose={() => setIsThemeCustomizerOpen(false)} 
          />
        </Suspense>
        </StoreHydrationGate>
      </Router>
    </ThemeProvider>
  );
}

export default App;